const ALLOWED_TAGS = new Set([
  "P", "BR", "STRONG", "B", "EM", "I", "U", "S", "DEL", "MARK", "A",
  "H1", "H2", "H3", "UL", "OL", "LI", "BLOCKQUOTE", "PRE", "CODE", "HR"
]);
const DANGEROUS_TAGS = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "TEMPLATE", "SVG", "MATH"]);
const MAX_DEPTH = 100;
const MAX_NODES = 10000;

export function sanitizeClipboardHtml(raw, { DOMParserImpl = globalThis.DOMParser } = {}) {
  if (typeof DOMParserImpl !== "function") {
    throw new Error("DOMParser is required to sanitize clipboard HTML");
  }

  const parser = new DOMParserImpl();
  const document = parser.parseFromString(`<!doctype html><html><body>${String(raw || "")}</body></html>`, "text/html");
  if (!document?.body) throw new Error("DOMParser failed to create an HTML document");
  sanitizeChildren(document.body, document);
  return document.body.innerHTML;
}

function sanitizeChildren(parent, document) {
  const budget = { count: 0 };
  const pending = Array.from(parent.childNodes || []).reverse().map((node) => ({ node, depth: 1, visited: false }));
  while (pending.length) {
    const { node, depth, visited } = pending.pop();
    if (!node.parentNode) continue;
    if (visited) {
      sanitizeElement(node, document);
      continue;
    }

    budget.count += 1;
    if (budget.count > MAX_NODES) {
      node.remove();
      continue;
    }
    if (node.nodeType === 8) {
      node.remove();
      continue;
    }
    if (node.nodeType !== 1) continue;

    const tag = node.tagName.toUpperCase();
    if (DANGEROUS_TAGS.has(tag)) {
      node.remove();
      continue;
    }
    if (depth >= MAX_DEPTH) {
      replaceWithSafeText(node, document, budget);
      continue;
    }

    pending.push({ node, depth, visited: true });
    const children = Array.from(node.childNodes || []);
    for (let index = children.length - 1; index >= 0; index -= 1) {
      pending.push({ node: children[index], depth: depth + 1, visited: false });
    }
  }
}

function sanitizeElement(node, document) {
  const tag = node.tagName.toUpperCase();
  if (tag === "SPAN" || tag === "FONT") replaceStyledInline(node, document);
  else if (!ALLOWED_TAGS.has(tag)) unwrap(node);
  else sanitizeAllowedElement(node, tag, document);
}

function replaceWithSafeText(node, document, budget) {
  let text = "";
  const pending = Array.from(node.childNodes || []).reverse();
  while (pending.length && budget.count < MAX_NODES) {
    const current = pending.pop();
    budget.count += 1;
    if (current.nodeType === 3) {
      text += current.nodeValue || "";
      continue;
    }
    if (current.nodeType !== 1 || DANGEROUS_TAGS.has(current.tagName.toUpperCase())) continue;
    const children = Array.from(current.childNodes || []);
    for (let index = children.length - 1; index >= 0; index -= 1) pending.push(children[index]);
  }
  if (text) node.replaceWith(document.createTextNode(text));
  else node.remove();
}

function replaceStyledInline(element, document) {
  const tags = semanticStyleTags(element.getAttribute("style"));

  if (!tags.length) {
    unwrap(element);
    return;
  }

  let outer = null;
  let current = null;
  for (const tag of tags) {
    const wrapper = document.createElement(tag);
    if (!outer) outer = wrapper;
    if (current) current.appendChild(wrapper);
    current = wrapper;
  }
  while (element.firstChild) current.appendChild(element.firstChild);
  element.replaceWith(outer);
}

function sanitizeAllowedElement(element, tag, document) {
  const href = tag === "A" ? element.getAttribute("href")?.trim() : null;
  const styleTags = semanticStyleTags(element.getAttribute("style"));
  if (styleTags.length) wrapChildren(element, styleTags, document);
  for (const attribute of Array.from(element.attributes || [])) element.removeAttribute(attribute.name);

  if (tag === "A" && /^(?:https?:\/\/|mailto:)/i.test(href || "")) {
    element.setAttribute("rel", "noopener noreferrer");
    element.setAttribute("target", "_blank");
    element.setAttribute("href", href);
  }
}

function semanticStyleTags(rawStyle) {
  const style = (rawStyle || "").toLowerCase();
  const tags = [];
  if (/font-weight\s*:\s*(?:bold|[7-9]00)/.test(style)) tags.push("strong");
  if (/font-style\s*:\s*italic/.test(style)) tags.push("em");
  if (/text-decoration[^:]*:[^;]*underline/.test(style)) tags.push("u");
  if (/text-decoration[^:]*:[^;]*line-through/.test(style)) tags.push("s");
  if (/background(?:-color)?\s*:/.test(style)) tags.push("mark");
  return tags;
}

function wrapChildren(element, tags, document) {
  let outer = null;
  let current = null;
  for (const tag of tags) {
    const wrapper = document.createElement(tag);
    if (!outer) outer = wrapper;
    if (current) current.appendChild(wrapper);
    current = wrapper;
  }
  while (element.firstChild) current.appendChild(element.firstChild);
  element.appendChild(outer);
}

function unwrap(element) {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) parent.insertBefore(element.firstChild, element);
  element.remove();
}
